<template>
  <div
    :class="wrapClasses"
    :style="styles">
    <div :class="tableClasses">

      <div
        v-if="hasSlotTitle"
        ref="title"
        :class="[cssClasses.title]"><slot name="title"/></div>

      <div
        v-if="showHeader"
        ref="header"
        :class="[cssClasses.header]"
        @mousewheel="handleMouseWheel">
        <table-head
          ref="thead"
          :columns="columnsDefs"
          :style-object="tableHeaderStyle" />
      </div>

      <div
        ref="body"
        :class="showBody ? [cssClasses.body] : [cssClasses.tip]"
        :style="bodyStyle"
        @scroll="handleBodyScroll"
      >

        <iSpin
          v-if="loading"
          ref="loader"
          :style="loaderStyle"
          fix
          size="large">
          <slot name="loading"/>
        </iSpin>

        <table-body
          v-if="showBody"
          key="table-body"
          ref="tbody"
          :columns="columnsDefs"
          :data="data"
          :style-object="tableStyle" />

        <table
          v-else
          key="table-body"
          cellspacing="0"
          cellpadding="0"
          border="0">
          <tbody>
            <tr>
              <td :style="{ height: bodyStyle.height, width: `${headerWidth}px` }">
                <span
                  v-if="!data || data.length === 0"
                  v-html="localeNoDataText"/>
                <span
                  v-else
                  v-html="localeNoFilteredDataText"/>
              </td>

            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="hasSlotFooter"
        ref="footer"
        :class="[cssClasses.footer]"><slot name="footer"/></div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { getStyle, deepCopy, getScrollBarSize } from 'iview/src/utils/assist';
import { on, off } from 'iview/src/utils/dom';

import elementResizeDetectorMaker from 'element-resize-detector';

import Locale from './mixins/locale';

import tableHead from './table-head.vue';
import tableBody from './table-body.vue';

const prefixCls = 'ivu-table';

export default {
  // beforeUpdate() { console.timedLog('table', 'beforeUpdate'); },
  // updated() { console.timedLog('table', 'updated'); },
  name: 'Table',
  mixins: [Locale],
  components: { tableHead, tableBody },
  provide() {
    return {
      table: this,
      cssClasses: this.cssClasses,
    };
  },
  props: {
    data: { type: Array, default: () => [] },
    columns: { type: Array, default: () => [] },
    sort: { type: Array, default: () => [] },
    filters: { type: Object, default: () => ({}) },

    width: { type: [Number, String] },
    height: { type: [Number, String] },
    stripe: { type: Boolean, default: false },
    border: { type: Boolean, default: false },

    showHeader: { type: Boolean, default: true },
    highlightRow: { type: Boolean, default: false },
    disabledHover: { type: Boolean, default: false },

    noDataText: { type: String },
    noFilteredDataText: { type: String },

    loading: { type: Boolean, default: false },

    size: {
      validator(value) {
        return ['small', 'large', 'default'].includes(value);
      },
    },
    rowClassName: {
      type: Function,
      default() {
        return '';
      },
    },
  },
  data() {
    return {
      ready: false,

      columnsWidth: {},

      tableWidth: 0,
      headerWidth: 0,
      headerHeight: 0,
      bodyHeight: 0,

      scrollBarWidth: getScrollBarSize(),
      showVerticalScrollBar: false,
      showHorizontalScrollBar: false,

      loaderStyle: {
        left: '0px',
        top: '0px',
      },

      highlightedRow: null,

    };
  },
  computed: {
    cssClasses() {
      return {
        title: `${prefixCls}-title`,
        header: `${prefixCls}-header`,
        body: `${prefixCls}-body`,
        tip: `${prefixCls}-tip`,
        footer: `${prefixCls}-footer`,
        tbody: `${prefixCls}-tbody`,

        row: `${prefixCls}-row`,
        rowHighlight: `${prefixCls}-row-highlight`,
        rowHover: `${prefixCls}-row-hover`,

        cell: `${prefixCls}-cell`,
        cellSort: `${prefixCls}-cell-sort`,
        cellEllipsis: `${prefixCls}-cell-ellipsis`,
        cellWithExpandExpanded: `${prefixCls}-cell-with-expand-expanded`,
        cellExpand: `${prefixCls}-cell-expand`,
        cellExpand: `${prefixCls}-cell-expand`,

        sort: `${prefixCls}-sort`,
        filter: `${prefixCls}-filter`,
        column: `${prefixCls}-column`,

        hidden: `${prefixCls}-hidden`,
        expandedHidden: `${prefixCls}-expanded-hidden`,
        overflowY: `${prefixCls}-overflowY`,
        overflowX: `${prefixCls}-overflowX`,
      };
    },

    columnsDefs() {
      return this.columns.map((c, index) => {
        const { render } = c;
        const column = {
          type: render ? 'render' : 'normal',
          ...c,
          index: index,
          computedWidth: c.width ? c.width : '', // update in handleResize()
        };
        return column;
      });
    },

    showBody() {
      const { localeNoDataText, localeNoFilteredDataText, data } = this;
      return !(
        (!!localeNoDataText && (!data || data.length === 0)) ||
        (!!localeNoFilteredDataText && (!data || data.length === 0))
      );
    },
    localeNoDataText() {
      return this.noDataText === undefined ? this.t('i.table.noDataText') : this.noDataText;
    },
    localeNoFilteredDataText() {
      return this.noFilteredDataText === undefined
        ? this.t('i.table.noFilteredDataText')
        : this.noFilteredDataText;
    },
    hasSlotTitle() {
      return this.$slots.title !== undefined;
    },
    hasSlotFooter() {
      return this.$slots.footer !== undefined;
    },

    wrapClasses() {
      return [
        `${prefixCls}-wrapper`,
        {
          [`${prefixCls}-hide`]: !this.ready,
          [`${prefixCls}-with-header`]: this.hasSlotTitle,
          [`${prefixCls}-with-footer`]: this.hasSlotFooter,
        },
      ];
    },
    tableClasses() {
      return [
        `${prefixCls}`,
        {
          [`${prefixCls}-${this.size}`]: !!this.size,
          [`${prefixCls}-border`]: this.border,
          [`${prefixCls}-stripe`]: this.stripe,
          [`${prefixCls}-with-fixed-top`]: !!this.height,
        },
      ];
    },

    styles() {
      let style = {};
      if (this.height) {
        const height = parseInt(this.height);
        style.height = `${height}px`;
      }
      if (this.width) style.width = `${this.width}px`;
      return style;
    },
    tableStyle() {
      let style = {};
      if (this.tableWidth !== 0) {
        let width = '';
        if (this.bodyHeight === 0) {
          width = this.tableWidth;
        } else {
          width = this.tableWidth - (this.showVerticalScrollBar ? this.scrollBarWidth : 0);
        }
        //                    const width = this.bodyHeight === 0 ? this.tableWidth : this.tableWidth - this.scrollBarWidth;
        style.width = `${width}px`;
      }
      return style;
    },
    tableHeaderStyle() {
      let style = {};
      if (this.tableWidth !== 0) {
        let width = '';
        width = this.tableWidth;
        style.width = `${width}px`;
      }
      return style;
    },
    bodyStyle() {
      let style = {};
      if (this.bodyHeight !== 0) {
        const height = this.bodyHeight;
        style.height = `${height}px`;
      }
      return style;
    },

    rowProps() {
      const Vue = this.$options._base;
      const data = {};

      this.highlightedRow = null;
      this.data.forEach((row, index) => {
        data[row.id] = {
          index,
          isHover: false,
          isDisabled: row.disabled || false,
          isChecked: row.checked || false,
          isExpanded: row.expanded || false,
          isHighlight: false,
        };
        // Allow computed property reactive
        Vue.util.defineReactive(data, row.id, data[row.id]);
        if (row.highlight) this.highlightedRow = row;
      });
      if (this.highlightedRow) data[this.highlightedRow.id].isHighlight = true;

      return data;
    },
    selected() {
      const { data, rowProps } = this;
      let selected = [];
      data.forEach((row) => {
        if (rowProps[row.id].isChecked && !rowProps[row.id].isDisable) selected.push(row);
      })
      return selected;
    },
    isSelectAll() {
      const { data, rowProps } = this;
      if (!data.length) return false;
      return data.every((row) => {
        const rowProp = rowProps[row.id];
        return rowProp.isDisabled || rowProp.isChecked;
      })
    },
    isSelectAllDisabled() {
      return !this.data.length;
    }
  },
  watch: {
    data: {
      handler() {
        this.handleResize();
        this.fixedHeader();
      },
    },
    columns: {
      handler() {
        this.handleResize();
        this.fixedHeader();
      },
    },
    selected(selected) {
      this.$emit('selected-change', selected);
    },
    height() {
      this.handleResize();
    },
    loading() {
      this.fixedLoader();
    },
    showHorizontalScrollBar() {
      this.handleResize();
    },
    showVerticalScrollBar() {
      this.handleResize();
    },
  },
  mounted() {
    this.handleResize();
    this.$nextTick(() => (this.ready = true));

    on(window, 'resize', this.handleResize);
    this.observer = elementResizeDetectorMaker();
    this.observer.listenTo(this.$el, this.handleResize);

    this.$on('on-visible-change', val => {
      if (val) {
        this.handleResize();
      }
    });
  },
  beforeDestroy() {
    off(window, 'resize', this.handleResize);
    this.observer.removeListener(this.$el, this.handleResize);
  },
  methods: {
    fixedLoader() {
      const { body } = this.$refs
      this.loaderStyle = { left: '0px', top: '0px' };
      if (!body) return;
      const left = body.scrollLeft + 'px';
      const top = body.scrollTop + 'px';
      this.loaderStyle = { left, top }
    },
    fixedHeader() {
      if (this.height) {
        this.$nextTick(() => {
          const titleHeight = parseInt(getStyle(this.$refs.title, 'height')) || 0;
          const headerHeight = parseInt(getStyle(this.$refs.header, 'height')) || 0;
          const footerHeight = parseInt(getStyle(this.$refs.footer, 'height')) || 0;
          this.bodyHeight = this.height - titleHeight - headerHeight - footerHeight;
          this.$nextTick(() => this.fixedBody());
        });
      } else {
        this.bodyHeight = 0;
        this.$nextTick(() => this.fixedBody());
      }
    },
    fixedBody() {
      if (this.$refs.header) {
        this.headerWidth = this.$refs.header.children[0].offsetWidth;
        this.headerHeight = this.$refs.header.children[0].offsetHeight;
        //this.showHorizontalScrollBar = this.headerWidth>this.$refs.header.offsetWidth;
      }

      if (!this.$refs.tbody || !this.data || this.data.length === 0) {
        this.showVerticalScrollBar = false;
      } else {
        let bodyContentEl = this.$refs.tbody.$el;
        let bodyEl = bodyContentEl.parentElement;
        let bodyContentHeight = bodyContentEl.offsetHeight;
        let bodyHeight = bodyEl.offsetHeight;

        this.showHorizontalScrollBar =
          bodyEl.offsetWidth <
          bodyContentEl.offsetWidth + (this.showVerticalScrollBar ? this.scrollBarWidth : 0);
        this.showVerticalScrollBar = this.bodyHeight
          ? bodyHeight - (this.showHorizontalScrollBar ? this.scrollBarWidth : 0) <
            bodyContentHeight
          : false;

        if (this.showVerticalScrollBar) {
          bodyEl.classList.add(this.cssClasses.overflowY);
        } else {
          bodyEl.classList.remove(this.cssClasses.overflowY);
        }
        if (this.showHorizontalScrollBar) {
          bodyEl.classList.add(this.cssClasses.overflowX);
        } else {
          bodyEl.classList.remove(this.cssClasses.overflowYX);
        }
      }
    },

    handleResize() {
      //let tableWidth = parseInt(getStyle(this.$el, 'width')) - 1;
      let tableWidth = this.$el.offsetWidth - 1;
      let columnsWidth = {};
      let sumMinWidth = 0;
      let hasWidthColumns = [];
      let noWidthColumns = [];
      let maxWidthColumns = [];
      let noMaxWidthColumns = [];
      this.columnsDefs.forEach(col => {
        if (col.width) {
          hasWidthColumns.push(col);
        } else {
          noWidthColumns.push(col);
          if (col.minWidth) {
            sumMinWidth += col.minWidth;
          }
          if (col.maxWidth) {
            maxWidthColumns.push(col);
          } else {
            noMaxWidthColumns.push(col);
          }
        }
        col.computedWidth = null;
      });

      let unUsableWidth = hasWidthColumns.map(cell => cell.width).reduce((a, b) => a + b, 0);
      let usableWidth =
        tableWidth -
        unUsableWidth -
        sumMinWidth -
        (this.showVerticalScrollBar ? this.scrollBarWidth : 0) -
        1;
      let usableLength = noWidthColumns.length;
      let columnWidth = 0;
      if (usableWidth > 0 && usableLength > 0) {
        columnWidth = parseInt(usableWidth / usableLength);
      }

      for (let i = 0; i < this.columnsDefs.length; i++) {
        const column = this.columnsDefs[i];
        let width = columnWidth + (column.minWidth ? column.minWidth : 0);
        if (column.width) {
          width = column.width;
        } else {
          if (column.computedWidth) {
            width = column.computedWidth;
          } else {
            if (column.minWidth > width) {
              width = column.minWidth;
            } else if (column.maxWidth < width) {
              width = column.maxWidth;
            }

            if (usableWidth > 0) {
              usableWidth -= width - (column.minWidth ? column.minWidth : 0);
              usableLength--;
              if (usableLength > 0) {
                columnWidth = parseInt(usableWidth / usableLength);
              } else {
                columnWidth = 0;
              }
            } else {
              columnWidth = 0;
            }
          }
        }

        column.computedWidth = width;

        columnsWidth[column.index] = {
          width: width,
        };
      }
      if (usableWidth > 0) {
        usableLength = noMaxWidthColumns.length;
        columnWidth = parseInt(usableWidth / usableLength);
        for (let i = 0; i < noMaxWidthColumns.length; i++) {
          const column = noMaxWidthColumns[i];
          let width = column.computedWidth + columnWidth;
          if (usableLength > 1) {
            usableLength--;
            usableWidth -= columnWidth;
            columnWidth = parseInt(usableWidth / usableLength);
          } else {
            columnWidth = 0;
          }

          column.computedWidth = width;

          columnsWidth[column.index] = {
            width: width,
          };
        }
      }

      this.tableWidth =
        this.columnsDefs.map(cell => cell.computedWidth).reduce((a, b) => a + b, 0) +
        (this.showVerticalScrollBar ? this.scrollBarWidth : 0) +
        1;
      this.columnsWidth = columnsWidth;
      this.fixedHeader();
    },
    handleBodyScroll(event) {
      if (this.showHeader) this.$refs.header.scrollLeft = event.target.scrollLeft;
      if (this.loading) { this.fixedLoader() }
      // this.hideColumnFilter();
    },
    handleMouseWheel(event) {
      const deltaX = event.deltaX;
      const $body = this.$refs.body;

      if (deltaX > 0) {
        $body.scrollLeft = $body.scrollLeft + 10;
      } else {
        $body.scrollLeft = $body.scrollLeft - 10;
      }
    },

    setHighlightedRow(current) {
      const old = this.highlightedRow;
      if (this.rowProps[current.id].isHighlight) {
        this.highlightedRow = null;
      } else {
        this.rowProps[current.id].isHighlight = true;
        this.highlightedRow = current;
      }
      if (old) this.rowProps[old.id].isHighlight = false;
      this.$emit('current-change', this.highlightedRow, old);
    },
    clearHighlightedRow() {
      const old = this.highlightedRow;
      if (!old) return;
      this.rowProps[old.id].isHighlight = false;
      this.$emit('current-change', null, old);
    },

    onToggleExpand(row) {
      const { rowProps } = this;
      const rowProp = rowProps[row.id];
      if (row.disableExpand) return;
      rowProp.isExpanded = !rowProp.isExpanded;
      this.$emit('expand', row, rowProp.isExpanded );
    },
    onSelectedAllChange(value) {
      const { rowProps } = this;
      Object
        .values(rowProps)
        .forEach((rowProp) => {
          if (!rowProp.isDisable) rowProp.isChecked = value;
        });
      const selected = this.selected;
      this.$emit('select-all', value, selected );
    },
    onSelectedChange(row, value) {
      const { rowProps } = this;
      const rowProp = rowProps[row.id];
      if (rowProp.isDisable) return;
      rowProp.isChecked = value;
      const selected = this.selected;
      this.$emit('select', row, value, selected );
    },
    onClickRow(row) {
      if (this.highlightRow) this.setHighlightedRow(row);
      this.$emit('row-click', row);
    },
    onDblClickRow(row) {
      if (this.highlightRow) this.setHighlightedRow(row);
      this.$emit('row-dbclick', row);
    },
    onSort({ column: { key }, order, shift = false }) {
      const sort = this.sort.filter(([attr]) => attr !== key);
      const isUpdate = this.sort.length != sort.length;

      if (!order) {
        this.$emit('sort-change', sort);
      } else if (shift || isUpdate) {
        sort.unshift([key, order]);
        this.$emit('sort-change', sort);
      } else {
        this.$emit('sort-change', [[key, order]]);
      }
    },
    onFilter(filter) {
      this.$emit('filter-change', filter);
    },
  },
};
</script>
